import React, { useEffect, useState, Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import ROUTE_NAVIGATION from './routeQuery';
import NotFoundPage from './NotFoundPage/NotFoundPage';
import Loader from '../components/Loader';
import PageTracker from '../components/PageTracker';
import {useRouterStatus} from "../context/RouterStatusContext";
import loadable from "@loadable/component";

const PageRenderer = loadable(() => import('../components/PageRenderer/PageRenderer'));
const Product = loadable(() => import('../screens/Product/ProductRenderer'));
const Category = loadable(() => import('../screens/Category/Category'));

const sanitizePath = (path) => {
    if (path.startsWith('//')) path = path.substring(2);
    if (path.startsWith('/')) path = path.substring(1);
    if (path.endsWith('/')) path = path.slice(0, -1);
    if (path === '') path = '/';
    return path;
};

// Pamięć podręczna ostatniego poprawnego stanu i ścieżki
let lastValidData = null;
let lastValidPath = '/';

const DynamicRouteResolver = ({ components, loaderComponent: LoaderComponent = Loader, errorComponent,  ...props }) => {

    const routerContext = useRouterStatus();

    const location = useLocation();
    const { pathname } = location;
    const path = sanitizePath(pathname);
    const [retryCount, setRetryCount] = useState(0);
    const [showNotFound, setShowNotFound] = useState(false);

    // Maksymalna liczba prób przed pokazaniem 404
    const MAX_RETRIES = 2;
    // Skonfiguruj zapytanie GraphQL
    const { loading, error, data, refetch } = useQuery(ROUTE_NAVIGATION, {
        variables: { url: path },
        fetchPolicy: "cache-and-network", // Użyj cache-and-network aby pokazać dane z cache i odświeżyć
        ssr: true,
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all' // Obsługuj błędy bez przerywania
    });
    // Efekt, który zapisuje ostatni poprawny stan
    useEffect(() => {
        if (!loading && !error && data && data.route) {
            lastValidData = data;
            lastValidPath = path;
        }
    }, [loading, error, data, path]);

    // Efekt obsługi błędów
    useEffect(() => {
        if (error && retryCount < MAX_RETRIES) {
            // Automatycznie ponów zapytanie po błędzie
            const retryTimeout = setTimeout(() => {
                refetch();
                setRetryCount(prev => prev + 1);
            }, 1000); // Ponów po 1 sekundzie
            
            return () => clearTimeout(retryTimeout);
        } else if ((error || (!data || !data.route)) && retryCount >= MAX_RETRIES) {
            // Po wyczerpaniu prób pokaż 404
            const notFoundTimeout = setTimeout(() => {
                setShowNotFound(true);
            }, 500);
            
            return () => clearTimeout(notFoundTimeout);
        }
    }, [error, retryCount, refetch, data]);

    // Extract page metadata for tracking - moved to component level
    const pageMetadata = useMemo(() => {
        const currentData = data || lastValidData;
        if (!currentData || !currentData.route) return { pageType: 'unknown' };
        
        try {
            const routeData = currentData.route;
            const pageType = routeData.__typename;
            
            // Default metadata
            const metadata = {
                pageType,
                uid: routeData.uid || '',
                url_key: routeData.url_key || path
            };
            // Add type-specific properties
            switch (pageType) {
                case 'SimpleProduct':
                case 'GroupedProduct':
                    return {
                        ...metadata,
                        name: routeData.name || '',
                        sku: routeData.sku || '',
                        price: routeData.price ? routeData.price.regularPrice?.amount?.value : null,
                        categories: routeData.categories?.map(cat => cat.name) || []
                    };
                case 'CategoryTree':
                    return {
                        ...metadata,
                        name: routeData.name || '',
                        products_count: routeData.products?.total_count || 0
                    };
                case 'CmsPage':
                    return {
                        ...metadata,
                        title: routeData.title || '',
                        identifier: routeData.identifier || ''
                    };
                default:
                    return metadata;
            }
        } catch (error) {
            console.error('Error extracting page metadata:', error);
            return { pageType: 'unknown' };
        }
    }, [data, lastValidData, path]);

    // Fallback dla SSR bez wcześniejszych danych
    if (typeof window === 'undefined' && !data && !lastValidData) {
        return <div id="ssr-loader-wrapper"><LoaderComponent /></div>;
    }

    // Wskaźnik ładowania jest obsługiwany przez SmoothPageTransition
    if (loading && typeof window !== 'undefined' && !window.document.querySelector('.smooth-page-transition-container')) {
        // Tylko poza kontekstem SmoothPageTransition pokaż loader
        return <div id="loader-wrapper"><LoaderComponent /></div>;
    }

    // Jeśli to błąd, ale mamy ostatnie poprawne dane i jesteśmy w trakcie ponownych prób
    if ((error || (!data || !data.route)) && lastValidData && retryCount < MAX_RETRIES) {
        // Podczas ponownych prób pokaż ostatnią poprawną zawartość zamiast błędu
        return renderContent(lastValidData);
    }
    if (data && data.route==null) {
        // Set 404 status in router context when route is null
        if (routerContext) routerContext.status = 404;
        return <div><NotFoundPage /></div>;
    }

    // Jeśli wszystkie próby zawiodły i showNotFound jest true
    if (showNotFound) {
        console.log('SET 404')
        // Set 404 status in router context when showing NotFound
        if (routerContext) routerContext.status = 404;
        return <div><NotFoundPage /></div>;
    }

    // Normalne renderowanie z danymi
    if (data && data.route) {
        return renderContent(data);
    }

    // Domyślny fallback podczas ładowania
    return <div id="default-loader"><LoaderComponent /></div>;

    // Funkcja renderująca zawartość w zależności od typu
    function renderContent(currentData) {
        if (!currentData || !currentData.route) return null;
        
        return (
            <Suspense fallback={<div id="fallback-loader"><LoaderComponent /></div>}>
                {(() => {
                    switch (currentData.route.__typename) {
                        case 'CmsPage': 
                            return (
                                <PageTracker 
                                    pageType="cms" 
                                    pageTitle={currentData.route.title || 'CMS Page'}
                                    pageData={pageMetadata}
                                >
                                    <PageRenderer routeData={currentData.route} />
                                </PageTracker>
                            );
                        case 'SimpleProduct':
                        case 'GroupedProduct': 
                            return (
                                <PageTracker 
                                    pageType="product" 
                                    pageTitle={currentData.route.name || 'Product Page'}
                                    pageData={pageMetadata}
                                    trackImpressionEvent={true}
                                >
                                    <Product routeData={currentData.route} />
                                </PageTracker>
                            );
                        case 'CategoryTree':
                            if (!currentData.route.uid) return null;
                            return (
                                <PageTracker 
                                    pageType="category" 
                                    pageTitle={currentData.route.name || 'Category Page'}
                                    pageData={pageMetadata}
                                    trackImpressionEvent={true}
                                >
                                    <div>
                                        <Category uid={currentData.route.uid} pageData={currentData.route} />
                                    </div>
                                </PageTracker>
                            );
                        default: 
                            // Tylko po wyczerpaniu wszystkich prób pokazujemy NotFound
                            if (showNotFound) {
                                return (
                                    <PageTracker pageType="notfound" pageTitle="Page Not Found">
                                        <div><NotFoundPage /></div>
                                    </PageTracker>
                                );
                            }
                            return <div id="fallback-loader"><LoaderComponent /></div>;
                    }
                })()}
            </Suspense>
        );
    }
};

DynamicRouteResolver.propTypes = {
    components: PropTypes.shape({}),
    loaderComponent: PropTypes.elementType,
    errorComponent: PropTypes.elementType,
};

export default DynamicRouteResolver;